import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { AlertTriangle, Church, Crown, Gem, ShieldCheck, Sparkles } from "@fleet/icons";
import { Badge } from "@fleet/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { useSession } from "@/lib/auth";
import {
  billingQueryKeys,
  type components,
  evaluateQuotas,
  formatLimit,
  formatUsed,
  interpretCheckout,
  type QuotaEvaluation,
  type QuotaKey,
  resolvePurchaseSurface,
} from "@agape/shared";
import { currentPurchasePlatform } from "@/lib/platform";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type BillingConfig = components["schemas"]["BillingConfigResponse"];
type BillingUsage = components["schemas"]["BillingUsageResponse"];
type CheckoutResponse = components["schemas"]["CheckoutResponse"];

/**
 * Plan and usage of THE CONGREGATION (MONETIZATION_QUOTAS, B2B variant).
 *
 * Agape does not bill the user but the church: what is painted here is the
 * plan of the active organization and its REAL usage measured on the server
 * (directory people, groups, churches in the tree, media and storage).
 *
 * - With monetization OFF (default) it shows an empty state and nothing else:
 *   zero behavior change.
 * - Advertising is never mentioned: Agape is TIER C of ADS_STRATEGY §9
 *   (institutional, no ads on any plan).
 */
export function PlanOverview() {
  const { t } = useI18n();
  const session = useSession();

  const configQuery = createApiQuery<BillingConfig, ErrorModel>(
    () => billingQueryKeys.config(),
    async () => api.GET("/api/billing/config", {}),
    () => ({ staleTime: 30_000 }),
  );

  const usageQuery = createApiQuery<BillingUsage, ErrorModel>(
    () => billingQueryKeys.usage(),
    async () => api.GET("/api/billing/usage", {}),
    () => ({
      staleTime: 15_000,
      enabled: !session.isVisitor() && configQuery.data?.enabled === true,
    }),
  );

  const checkoutMutation = createApiMutation(async (vars: { tier: string }) =>
    // The surface travels in the request: the server does not return a payment
    // URL to a store binary (`monetization::purchase`). Sending the field
    // always is what lets its ABSENCE fail closed there.
    api.POST("/api/billing/checkout", {
      body: { tier: vars.tier, platform: currentPurchasePlatform() },
    }),
  );

  const config = () => configQuery.data;
  const usage = () => usageQuery.data;

  /**
   * PLATFORM CLAMP — the client half.
   *
   * Without this, three rows of the admin panel (`monetization.enabled`,
   * `payments.enabled`, `payments.provider=stripe`) make an **already
   * installed APK** paint «Upgrade» and navigate to Stripe: grounds for
   * Google Play removal, without redeploying and without re-uploading. The
   * server already closes it; this closes the other half, because the
   * published binary does not learn that the server changed and because a
   * button that always fails is already, in itself, a visible purchase
   * surface inside the binary.
   */
  const purchaseSurface = createMemo(() =>
    resolvePurchaseSurface({
      platform: currentPurchasePlatform(),
      enabled: config()?.enabled ?? false,
      paymentsEnabled: config()?.paymentsEnabled ?? false,
      provider: config()?.provider ?? "none",
    }),
  );

  const currentTierId = () => usage()?.tier ?? config()?.defaultTier;

  const currentTier = createMemo(() => config()?.tiers.find((tier) => tier.id === currentTierId()));

  const evaluations = createMemo<QuotaEvaluation[]>(() => {
    const u = usage();
    if (!u) {
      return [];
    }
    return evaluateQuotas(
      {
        storage_bytes: u.limits.storageBytes ?? null,
        items: u.limits.maxItems ?? null,
        collections: u.limits.maxCollections ?? null,
        orgs: u.limits.maxOrgs ?? null,
        attachments: u.limits.maxAttachments ?? null,
      },
      {
        storage_bytes: u.usage.storageBytes,
        items: u.usage.items,
        collections: u.usage.collections,
        orgs: u.usage.orgs,
        attachments: u.usage.attachments,
      },
    );
  });

  const hasExceeded = () => (usage()?.exceeded.length ?? 0) > 0;
  const hasRejections = () => (usage()?.rejectedCount ?? 0) > 0;

  /** Agape vocabulary: the canonical keys speak of the congregation. */
  const quotaLabel = (key: QuotaKey): string => {
    switch (key) {
      case "storage_bytes": {
        return t(msg`Storage`);
      }
      case "items": {
        return t(msg`People in the directory`);
      }
      case "collections": {
        return t(msg`Groups`);
      }
      case "orgs": {
        return t(msg`Churches in the tree`);
      }
      default: {
        return t(msg`Photos and receipts`);
      }
    }
  };

  const featureLabel = (feature: string): string => {
    switch (feature) {
      case "export": {
        return t(msg`Data export (always included)`);
      }
      case "multi-church": {
        return t(msg`Multi-church tree`);
      }
      case "advanced-permissions": {
        return t(msg`Advanced scoped permissions`);
      }
      case "projection-p2p": {
        return t(msg`LAN projection`);
      }
      case "finances": {
        return t(msg`Finances and reports`);
      }
      case "photo-storage": {
        return t(msg`Photo storage`);
      }
      case "devotional-tracking": {
        return t(msg`Devotional follow-up`);
      }
      case "multi-org": {
        return t(msg`Multiple organizations`);
      }
      case "federated-roles": {
        return t(msg`Federated roles`);
      }
      default: {
        return t(msg`Sub-tree reports`);
      }
    }
  };

  const startUpgrade = (tierId: string) => {
    checkoutMutation.mutate(
      { tier: tierId },
      {
        onSuccess: (result: CheckoutResponse) => {
          // `interpretCheckout` REQUIRES the platform: without it it does not
          // compile. It is the last gate before `location.assign`, and returns
          // `blocked` when the surface does not admit that purchase.
          const action = interpretCheckout(
            {
              provider: result.provider,
              checkoutUrl: result.checkoutUrl ?? null,
              manual: result.manual,
            },
            currentPurchasePlatform(),
          );
          if (action.kind === "redirect") {
            globalThis.location.assign(action.url);
          } else if (action.kind === "blocked") {
            showToast(
              t(msg`Purchases are not available in this app. Manage your plan on the website.`),
              "info",
            );
          } else {
            showToast(
              t(msg`Plan changes are handled manually. Please contact us to upgrade your church.`),
              "info",
            );
          }
        },
        onError: () => {
          showToast(t(msg`Could not start the upgrade. Try again later.`), "error");
        },
      },
    );
  };

  return (
    <div class="space-y-6" data-testid="plan-overview">
      {/* P23/CC-60 · I3: «no hay respuesta» ≠ «no hay nada». */}
      <Show when={configQuery.isError}>
        <QueryErrorState
          testId="billing-plan-error"
          compact
          onRetry={() => void configQuery.refetch()}
        />
      </Show>
      <Show when={!configQuery.isError}>
        <Show
          when={!configQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <Skeleton class="h-24 w-full rounded-xl" />
              <Skeleton class="h-48 w-full rounded-xl" />
            </div>
          }
        >
          <Show
            when={config()?.enabled}
            fallback={
              <EmptyState
                icon={Gem}
                title={t(msg`Plans are not available yet`)}
                description={t(msg`This app does not have subscription plans enabled.`)}
              />
            }
          >
            {/* Plan actual de la congregación */}
            <Card data-testid="plan-current-tier">
              <CardHeader>
                <CardTitle class="flex items-center gap-2">
                  <Crown class="h-5 w-5 text-primary" />
                  {t(msg`Your church plan`)}
                </CardTitle>
              </CardHeader>
              <CardContent class="flex items-center justify-between gap-4">
                <div>
                  <div class="text-2xl font-bold" data-testid="plan-tier-label">
                    {currentTier()?.label ?? config()?.defaultTier}
                  </div>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`Billed per active congregation.`)}
                  </p>
                </div>
                <Badge variant="secondary" class="gap-1">
                  <Church class="h-3 w-3" />
                  {t(msg`Congregation`)}
                </Badge>
              </CardContent>
            </Card>

            {/* Señal de quota excedida / escrituras rechazadas */}
            <Show when={hasExceeded() || hasRejections()}>
              <Card class="border-destructive/50" data-testid="quota-exceeded-banner">
                <CardContent class="p-4 flex items-start gap-3">
                  <AlertTriangle class="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div class="min-w-0 text-sm">
                    <p class="font-semibold">
                      {t(msg`Your congregation reached a limit of its plan`)}
                    </p>
                    <p class="text-muted-foreground">
                      <Show
                        when={hasRejections()}
                        fallback={t(
                          msg`New records may not sync until you free up space or upgrade.`,
                        )}
                      >
                        {t(
                          msg`Some recent changes could not be saved to the server. Free up space or upgrade the plan.`,
                        )}
                      </Show>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Show>

            {/* Uso vs límites */}
            <Show
              when={!session.isVisitor()}
              fallback={
                <Card>
                  <CardContent class="p-6 text-sm text-muted-foreground">
                    {t(msg`Sign in with your church account to see its usage.`)}
                  </CardContent>
                </Card>
              }
            >
              <Card data-testid="plan-usage">
                <CardHeader>
                  <CardTitle>{t(msg`Usage`)}</CardTitle>
                </CardHeader>
                <CardContent class="space-y-4">
                  <Show
                    when={!usageQuery.isLoading}
                    fallback={
                      <div class="space-y-3">
                        <Skeleton class="h-8 w-full rounded" />
                        <Skeleton class="h-8 w-full rounded" />
                        <Skeleton class="h-8 w-full rounded" />
                      </div>
                    }
                  >
                    <For each={evaluations()}>
                      {(evaluation) => (
                        <div class="space-y-1" data-testid={`quota-row-${evaluation.key}`}>
                          <div class="flex items-center justify-between text-sm">
                            <span class="font-medium">{quotaLabel(evaluation.key)}</span>
                            <span
                              classList={{
                                "text-muted-foreground": !evaluation.nearLimit,
                                "text-warning": evaluation.nearLimit && !evaluation.exceeded,
                                "text-destructive font-semibold": evaluation.exceeded,
                              }}
                              data-testid={`quota-value-${evaluation.key}`}
                            >
                              {formatUsed(evaluation.key, evaluation.used)} /{" "}
                              {formatLimit(evaluation.key, evaluation.limit)}
                            </span>
                          </div>
                          <div class="h-2 w-full rounded-full bg-muted overflow-hidden">
                            <div
                              class="h-full rounded-full transition-all"
                              classList={{
                                "bg-primary": !evaluation.nearLimit,
                                "bg-warning": evaluation.nearLimit && !evaluation.exceeded,
                                "bg-destructive": evaluation.exceeded,
                              }}
                              style={{ width: `${Math.round(evaluation.ratio * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </For>
                  </Show>
                </CardContent>
              </Card>
            </Show>

            {/* Portabilidad: promesa explícita, no letra pequeña */}
            <Card data-testid="plan-portability">
              <CardContent class="p-4 flex items-start gap-3 text-sm">
                <ShieldCheck class="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p class="text-muted-foreground">
                  {t(
                    msg`Your data is always yours: exporting your directory, finances and history is included in every plan, including the free one.`,
                  )}
                </p>
              </CardContent>
            </Card>

            {/* Planes disponibles + upgrade */}
            <Card data-testid="plan-tiers">
              <CardHeader>
                <CardTitle class="flex items-center gap-2">
                  <Sparkles class="h-5 w-5 text-primary" />
                  {t(msg`Available plans`)}
                </CardTitle>
              </CardHeader>
              <CardContent class="grid gap-3 sm:grid-cols-2">
                <For each={config()?.tiers ?? []}>
                  {(tier) => (
                    <div
                      class="rounded-xl border p-4 space-y-2"
                      classList={{ "border-primary": tier.id === currentTierId() }}
                      data-testid={`plan-tier-${tier.id}`}
                    >
                      <div class="flex items-center justify-between">
                        <span class="font-bold">{tier.label}</span>
                        <Show when={tier.id === currentTierId()}>
                          <Badge>{t(msg`Current`)}</Badge>
                        </Show>
                      </div>
                      <ul class="text-xs text-muted-foreground space-y-1">
                        <li>
                          {t(msg`Churches in the tree`)}:{" "}
                          {formatLimit("orgs", tier.limits.maxOrgs ?? null)}
                        </li>
                        <li>
                          {t(msg`People in the directory`)}:{" "}
                          {formatLimit("items", tier.limits.maxItems ?? null)}
                        </li>
                        <li>
                          {t(msg`Storage`)}:{" "}
                          {formatLimit("storage_bytes", tier.limits.storageBytes ?? null)}
                        </li>
                      </ul>
                      <ul class="text-xs space-y-1" data-testid={`plan-features-${tier.id}`}>
                        <For each={tier.features}>
                          {(feature) => <li>· {featureLabel(feature)}</li>}
                        </For>
                      </ul>
                      {/* El botón solo existe donde la compra es legal: en el
                          binario de tienda no se pinta «Upgrade». */}
                      <Show
                        when={
                          purchaseSurface().offersPurchase &&
                          !session.isVisitor() &&
                          tier.id !== currentTierId()
                        }
                      >
                        <Button
                          size="sm"
                          class="w-full"
                          disabled={checkoutMutation.isPending}
                          onClick={() => {
                            startUpgrade(tier.id);
                          }}
                          data-testid={`plan-upgrade-${tier.id}`}
                        >
                          {t(msg`Upgrade`)}
                        </Button>
                      </Show>
                    </div>
                  )}
                </For>
              </CardContent>
            </Card>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
